/**
 * Sprint P32 · Cockpit Diagnostics panel — Leapfrog/Vulcan-style HUD.
 * Always available; shows CRS, model origin, bounding box, camera state,
 * near/far clipping, vertical exaggeration and coordinate transform info.
 */
import { Info, Warning } from "@phosphor-icons/react";


export function DiagnosticsPanel({ diag, onClose }) {
  const fmt = (v, digits = 2) => (Number.isFinite(v) ? v.toFixed(digits) : String(v));
  const vec = (v, digits = 2) => v
    ? `${fmt(v.x, digits)}, ${fmt(v.y, digits)}, ${fmt(v.z, digits)}` : "—";

  const easting = diag.origin
    ? `${fmt(diag.origin.easting, 1)}` : "—";
  const northing = diag.origin
    ? `${fmt(diag.origin.northing, 1)}` : "—";
  const rl = diag.origin
    ? `${fmt(diag.origin.elevation, 1)}` : "—";

  const eastingRange = diag.worldBBox
    ? `${fmt(diag.worldBBox.xMin, 0)} → ${fmt(diag.worldBBox.xMax, 0)}` : "—";
  const northingRange = diag.worldBBox
    ? `${fmt(diag.worldBBox.yMin, 0)} → ${fmt(diag.worldBBox.yMax, 0)}` : "—";
  const elevationRange = diag.worldBBox
    ? `${fmt(diag.worldBBox.zMin, 0)} → ${fmt(diag.worldBBox.zMax, 0)}` : "—";

  const totalObjects = (diag.blocksRequested || 0) + (diag.holesTotal || 0)
    + (diag.geoMeshCount || 0) + (diag.mineDesignPresent ? 1 : 0);
  const visibleObjects = (diag.blocksRendered || 0) + (diag.holesActive || 0)
    + (diag.geoMeshVisible || 0) + (diag.mineDesignVisible ? 1 : 0);

  const rows = [
    ["CRS",                        diag.crs || "local (project CRS not set)"],
    ["Vertical datum",              diag.datum || "—"],
    ["Model origin · Easting",      easting],
    ["Model origin · Northing",     northing],
    ["Model origin · RL",           rl],
    ["Easting range (m)",           eastingRange],
    ["Northing range (m)",          northingRange],
    ["Elevation range (m)",         elevationRange],
    ["Bounding box diagonal",       diag.diagonal ? `${fmt(diag.diagonal, 1)} m` : "—"],
    ["Local bbox size (m)",         diag.localSize
      ? `${fmt(diag.localSize.x, 0)} × ${fmt(diag.localSize.y, 0)} × ${fmt(diag.localSize.z, 0)}`
      : "—"],
    ["Object count total",          totalObjects],
    ["Visible object count",        visibleObjects],
    ["Blocks rendered / total",     `${diag.blocksRendered ?? 0} / ${diag.blocksRequested ?? 0}`],
    ["Blocks filtered out",         diag.blocksFiltered ?? 0],
    ["Invalid block coords",        diag.invalidBlocks ?? 0],
    ["Drillholes shown / total",    `${diag.holesActive ?? 0} / ${diag.holesTotal ?? 0}`],
    ["Outlier holes",               diag.outlierHoles ?? 0],
    ["Camera target",               vec(diag.cameraTarget, 1)],
    ["Camera position",             vec(diag.cameraPos, 1)],
    ["Near / Far clip (scene m)",   diag.near != null
      ? `${fmt(diag.near, 2)} / ${fmt(diag.far, 0)}` : "—"],
    ["Renderer size (px)",          diag.rendererSize ? `${diag.rendererSize.w} × ${diag.rendererSize.h}` : "—"],
    ["Vertical exaggeration",       `${diag.vExag ?? 1}×`],
    ["LOD mode",                    diag.lodMode === "auto"
                                        ? `auto → level ${diag.lodLevel} (stride ${diag.lodStride})`
                                        : `manual level ${diag.lodLevel} (stride ${diag.lodStride})`],
    ["Blocks after LOD stride",     `${diag.blocksLODCount ?? 0} / ${diag.blocksRequested ?? 0}`],
    ["Fit mode",                    diag.fitMode || "—"],
    ["Coordinate transform",        diag.origin ? "world → local (worldToScene)" : "identity (no origin)"],
    ["WebGL error",                 diag.glError || "OK"],
  ];

  const flags = [];
  if (diag.diagonal > 100000) flags.push("Bounding-box diagonal > 100 km — check units");
  if (diag.diagonal != null && diag.diagonal < 1 && diag.blocksRendered > 0)
    flags.push("Local-model diagonal < 1 m — likely coord transform mis-alignment");
  if (diag.invalidBlocks > 0) flags.push(`${diag.invalidBlocks} block(s) rejected — invalid coords`);
  if (diag.cameraPos && diag.diagonal &&
      Math.hypot(diag.cameraPos.x, diag.cameraPos.y, diag.cameraPos.z) > 100 * diag.diagonal)
    flags.push("Camera distance > 100 × diagonal — press Fit");
  if (!diag.crs) flags.push("Project CRS not declared — use local axes only");

  return (
    <div className="absolute top-3 right-3 z-40 w-[340px] bg-slate-950/95 border border-sky-500/50 rounded-md shadow-2xl text-[10px] font-mono text-slate-200 backdrop-blur"
         data-testid="cockpit-diagnostics">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800 bg-gradient-to-r from-sky-900/60 to-slate-900/60 rounded-t-md">
        <div className="flex items-center gap-2">
          <Info size={12} className="text-sky-400" weight="duotone" />
          <div className="text-sky-300 font-bold uppercase tracking-widest text-[10px]">
            Viewport Diagnostics
          </div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white text-[14px]"
                data-testid="cockpit-diag-close">
          ×
        </button>
      </div>
      <div className="p-3 space-y-0.5 max-h-[70vh] overflow-y-auto">
        {rows.map(([k, v]) => (
          <div key={k} className="grid grid-cols-[1fr_auto] gap-x-2 border-b border-slate-800/50 pb-0.5">
            <span className="text-slate-500 text-[9.5px]">{k}</span>
            <span className="text-right text-slate-100 truncate max-w-[190px]" title={String(v)}>{v}</span>
          </div>
        ))}
        {flags.length > 0 && (
          <div className="mt-2 pt-2 border-t border-rose-900/60 space-y-0.5">
            <div className="text-rose-400 uppercase tracking-widest text-[9px] mb-1">
              Alerts
            </div>
            {flags.map((f, i) => (
              <div key={i} className="text-rose-300 text-[10px] flex items-start gap-1">
                <Warning size={10} weight="fill" className="shrink-0 mt-0.5" /> {f}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
